import * as React from "react";
import { inject } from "mobx-react";
import { Viewport, ViewMedium } from "bernie-view";
import { decorateDestinationHeroHtml, Sources } from "../DestinationHeroUtilities";
import { WizardHeroWithHeadingsProps } from "../typings";
import { Media } from "typings/microserviceModels/destination-hero-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkFigure, UitkImage } from "uitk-react-images";

export const WizardHeroWithHeadings = inject(
  "wizardState",
  "thumbor",
  "compositionStore"
)((props: WizardHeroWithHeadingsProps) => {
  const { flexTemplateNode, model, context, thumbor, wizardState, compositionStore } = props;
  const { id } = flexTemplateNode;
  const { content } = model;
  const { formatText } = useLocalization();

  const heading = compositionStore?.pageHeading;
  const subHeading = compositionStore?.pageSubHeadline;
  const hasHeadings = heading || subHeading;

  const firstImage = content && content.media && content.media[0];
  const fallbackImage: Media = {
    // Empty fields are required to adhere to our autogenerated typing for Media.
    cardIdentifier: "",
    mediaUrl: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaUrlTrimmed: "",
    mediaUrlOriginal: "//media.expedia.com/media/content/shared/images/travelguides/sem-hotels-tablet.jpg",
    mediaAlt: "Default HTG hotel image",
    origin: "Expedia",
    source: "",
    author: "",
    authorUrl: "",
    license: { name: "", suite: "", url: "" },
    mediaType: "Image",
  };
  const selectedHeroImage =
    firstImage && !firstImage.mediaUrl.includes("TE_Clouds_card_r1.jpg") ? firstImage : fallbackImage;

  const thumborizedHeroImageUrl = (): string => {
    const { mediaUrl } = selectedHeroImage;
    if (wizardState) {
      wizardState.globalWizardState.config.heroImageSrc = mediaUrl;
    }

    if (!thumbor) {
      return mediaUrl;
    }

    const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;

    return isMobile
      ? thumbor.safelyGenerateThumborizedImageUrl(mediaUrl, 1400, 600, context, AspectRatioEnum.R_21_9, true, 80)
      : thumbor.safelyGenerateThumborizedImageUrl(mediaUrl, 1400, 600, context, AspectRatioEnum.R_21_9, true, 100);
  };

  const { source } = selectedHeroImage;
  const isTroverSource = source === Sources.Trover;
  const isWikimediaSource = source === Sources.Wikimedia;
  const isAttributionRequired = isTroverSource || isWikimediaSource;

  const heroTitle = (): string => {
    if (!isWikimediaSource) {
      return "";
    }

    return formatText(
      "hero.attribution.wikimediaAlt",
      selectedHeroImage.mediaAlt,
      selectedHeroImage.author,
      selectedHeroImage.license && selectedHeroImage.license.name
    );
  };

  return (
    <div className="WizardHeroWithHeadings" id={id}>
      <UitkFigure className="heroImageFigure">
        <UitkImage alt={heroTitle()} src={thumborizedHeroImageUrl()} />
        <Viewport>
          <ViewMedium>
            <>
              {hasHeadings && (
                <UitkScrim type={UitkScrimType.BOTTOM}>
                  <UitkSpacing padding={{ inline: "six", block: "six" }}>
                    <UitkLayoutPosition type="absolute" position={{ bottom: "zero" }}>
                      <div className="heroImageHeadings">
                        {heading && (
                          <UitkHeading level={1} size={800}>
                            {heading}
                          </UitkHeading>
                        )}
                        {subHeading && (
                          <UitkSpacing padding={{ blockstart: "two" }}>
                            <UitkHeading level={2} size={600}>
                              {subHeading}
                            </UitkHeading>
                          </UitkSpacing>
                        )}
                      </div>
                    </UitkLayoutPosition>
                  </UitkSpacing>
                </UitkScrim>
              )}
            </>
          </ViewMedium>
        </Viewport>
      </UitkFigure>
      <AttributionText
        isAttributionRequired={isAttributionRequired}
        selectedHeroImage={selectedHeroImage}
        isTroverSource={isTroverSource}
      />
    </div>
  );
});

interface AttributionTextProps {
  isAttributionRequired: boolean;
  selectedHeroImage: Media;
  isTroverSource: boolean;
}

const AttributionText: React.FC<AttributionTextProps> = ({
  isAttributionRequired,
  selectedHeroImage,
  isTroverSource,
}): React.ReactElement | null => {
  const { formatText } = useLocalization();

  if (!isAttributionRequired) {
    return null;
  }

  const { mediaUrlOriginal, author, authorUrl, license } = selectedHeroImage;
  const licenseSuite = license && license.suite;
  const licenseUrl = license && license.url;

  const htmlToDisplay = () => {
    if (isTroverSource) {
      const troverPhotoAnchor = formatText("hero.attribution.troverLink", author, mediaUrlOriginal);

      return decorateDestinationHeroHtml(troverPhotoAnchor);
    } else {
      const wikimediaPhotoAnchor = formatText(
        "hero.attribution.wikimediaLink",
        mediaUrlOriginal,
        authorUrl,
        author,
        licenseUrl,
        licenseSuite
      );

      return decorateDestinationHeroHtml(wikimediaPhotoAnchor);
    }
  };

  return (
    <UitkSpacing padding={{ inline: "three", block: "two" }}>
      <span className="uitk-type-200" dangerouslySetInnerHTML={htmlToDisplay()} />
    </UitkSpacing>
  );
};
